import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';

export interface Category {
  id: string;
  user_id: string | null;
  name: string;
  type: 'income' | 'expense';
  color: string;
  icon: string | null;
  is_default: boolean;
  created_at: string;
}

export const PRESET_COLORS = [
  '#22c55e', '#ef4444', '#f97316', '#eab308',
  '#3b82f6', '#8b5cf6', '#ec4899', '#06b6d4',
  '#84cc16', '#f43f5e', '#14b8a6', '#64748b',
];

export const PRESET_ICONS = [
  'ri-tag-line', 'ri-shopping-bag-line', 'ri-restaurant-line', 'ri-car-line',
  'ri-home-line', 'ri-heart-pulse-line', 'ri-gamepad-line', 'ri-briefcase-line',
  'ri-gift-line', 'ri-plane-line', 'ri-wifi-line', 'ri-graduation-cap-line',
  'ri-star-line', 'ri-music-line', 'ri-tools-line', 'ri-store-line',
];

export const useCategories = (filterType?: 'income' | 'expense') => {
  const { user } = useAuth();
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);

  // ── Security: clear stale categories immediately when user changes or logs out ──
  const prevUserIdRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    const currentUserId = user?.id;
    if (prevUserIdRef.current !== currentUserId) {
      if (prevUserIdRef.current !== undefined || currentUserId === undefined) {
        setCategories([]);
      }
      prevUserIdRef.current = currentUserId;
    }
  }, [user?.id]);

  const fetchCategories = useCallback(async () => {
    if (!user) { setCategories([]); setLoading(false); return; }
    setLoading(true);
    let query = supabase
      .from('categories')
      .select('*')
      .order('is_default', { ascending: false })
      .order('name', { ascending: true });

    if (filterType) query = query.eq('type', filterType);

    const { data } = await query;
    setCategories((data as Category[]) ?? []);
    setLoading(false);
  }, [user, filterType]);

  useEffect(() => { fetchCategories(); }, [fetchCategories]);

  const createCategory = async (
    values: Pick<Category, 'name' | 'type' | 'color' | 'icon'>
  ): Promise<{ success: boolean; category?: Category }> => {
    if (!user) return { success: false };
    const { data, error } = await supabase
      .from('categories')
      .insert({ ...values, user_id: user.id, is_default: false })
      .select()
      .maybeSingle();
    if (error) return { success: false };
    await fetchCategories();
    return { success: true, category: data as Category };
  };

  const updateCategory = async (
    id: string,
    values: Partial<Pick<Category, 'name' | 'color' | 'icon'>>
  ): Promise<{ success: boolean }> => {
    const { error } = await supabase
      .from('categories')
      .update(values)
      .eq('id', id)
      .eq('user_id', user?.id ?? '');
    if (error) return { success: false };
    await fetchCategories();
    return { success: true };
  };

  const deleteCategory = async (id: string): Promise<{ success: boolean; message: string }> => {
    // Check for existing transactions using this category by name
    const cat = categories.find((c) => c.id === id);
    if (!cat) return { success: false, message: 'Categoria não encontrada.' };

    const { count } = await supabase
      .from('transactions')
      .select('id', { count: 'exact', head: true })
      .eq('user_id', user?.id ?? '')
      .eq('category', cat.name);

    if ((count ?? 0) > 0) {
      return {
        success: false,
        message: `Esta categoria está em uso em ${count} lançamento(s). Remova os vínculos antes de excluir.`,
      };
    }

    const { error } = await supabase
      .from('categories')
      .delete()
      .eq('id', id)
      .eq('user_id', user?.id ?? '');

    if (error) return { success: false, message: 'Erro ao excluir categoria.' };
    await fetchCategories();
    return { success: true, message: 'Categoria excluída.' };
  };

  const incomeCategories = categories.filter((c) => c.type === 'income');
  const expenseCategories = categories.filter((c) => c.type === 'expense');

  return {
    categories,
    incomeCategories,
    expenseCategories,
    loading,
    createCategory,
    updateCategory,
    deleteCategory,
    refetch: fetchCategories,
  };
};
